import { isNumber, isPlainObject, isString, toString } from "lodash-es";

import type { DesktopRecorderTranscriptTurn } from "@/store/audio/recording/media-transcriber/transcriber.types";
import {
  MediaRecorderAudioChunk,
  MediaRecorderMimeType,
} from "@/store/audio/recording/media-types";

type RawDesktopTurn = {
  turn_order?: unknown;
  transcript?: unknown;
  timestamp?: unknown;
  turn_is_formatted?: unknown;
  end_of_turn?: unknown;
  end_of_turn_confidence?: unknown;
  words?: unknown;
};

type NormalizedWord = DesktopRecorderTranscriptTurn["words"][number];

type RawDesktopWord = {
  text?: unknown;
  start?: unknown;
  end?: unknown;
  confidence?: unknown;
};

const createEmptyWord = (): NormalizedWord => ({
  text: "",
  startMs: 0,
  endMs: 0,
  confidence: 0,
});

const toFiniteNumber = (value: unknown): number | undefined => {
  if (!isNumber(value) || !Number.isFinite(value)) {
    return undefined;
  }

  return value;
};

const toFiniteNumberOr = (value: unknown, fallback: number): number =>
  toFiniteNumber(value) ?? fallback;

const toStringOrEmpty = (value: unknown): string => (isString(value) ? value : "");

const toTrimmedText = (value: unknown): string => toString(value ?? "").trim();

const normalizeWord = (candidate: unknown): NormalizedWord => {
  if (!isPlainObject(candidate)) {
    return createEmptyWord();
  }

  const word = candidate as RawDesktopWord;

  return {
    text: toTrimmedText(word.text),
    startMs: toFiniteNumberOr(word.start, 0),
    endMs: toFiniteNumberOr(word.end, 0),
    confidence: toFiniteNumberOr(word.confidence, 0),
  };
};

const normalizeWords = (value: unknown): NormalizedWord[] => {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }

  return value.map(normalizeWord);
};

export function buildRecorderChunkFromPcm({
  samples,
  sampleRate,
  mimeType = MediaRecorderMimeType.Pcm16,
}: {
  samples: Int16Array;
  sampleRate: number;
  mimeType?: MediaRecorderMimeType;
}): MediaRecorderAudioChunk {
  const durationMs = Math.round((samples.length / sampleRate) * 1000);
  const pcmBuffer = samples.buffer.slice(
    samples.byteOffset,
    samples.byteOffset + samples.byteLength
  );
  const blob = new Blob([pcmBuffer], { type: mimeType });

  return {
    blob,
    durationMs,
    mimeType,
    byteLength: blob.size,
  };
}

export function normalizeDesktopTranscriptTurn({
  raw,
  fallbackTimestamp = Date.now(),
}: {
  raw: unknown;
  fallbackTimestamp?: number;
}): DesktopRecorderTranscriptTurn {
  const turn: RawDesktopTurn = isPlainObject(raw) ? (raw as RawDesktopTurn) : {};
  const turnOrder = toFiniteNumber(turn.turn_order) ?? 0;
  const timestampMs = toFiniteNumber(turn.timestamp) ?? fallbackTimestamp;
  const transcript = toStringOrEmpty(turn.transcript);
  const words = normalizeWords(turn.words);

  return {
    turnOrder,
    transcript,
    timestampMs,
    isFormatted: Boolean(turn.turn_is_formatted),
    isEndOfTurn: Boolean(turn.end_of_turn),
    endOfTurnConfidence: toFiniteNumberOr(turn.end_of_turn_confidence, 0),
    words,
    raw,
  };
}

const DESKTOP_PCM_MIME_PREFIX = "application/x-recall-pcm";
const DEFAULT_PCM_SAMPLE_RATE = 16000;
const WAV_MIME_TYPE = "audio/wav";

const isPcmMimeType = (value?: string | null): boolean => {
  if (!value) return false;
  return (
    value.startsWith(DESKTOP_PCM_MIME_PREFIX) ||
    value.toLowerCase() === MediaRecorderMimeType.Pcm16.toLowerCase()
  );
};

const concatenateInt16Arrays = (chunks: Int16Array[]): Int16Array => {
  const totalLength = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const combined = new Int16Array(totalLength);
  let offset = 0;

  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }

  return combined;
};

const encodePcm16ToWav = (samples: Int16Array, sampleRate: number): Blob => {
  const headerSize = 44;
  const bytesPerSample = 2;
  const bitsPerSample = 16;
  const channels = 1;
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i++) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channels * bytesPerSample, true);
  view.setUint16(32, channels * bytesPerSample, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = headerSize;
  for (let i = 0; i < samples.length; i++) {
    view.setInt16(offset, samples[i], true);
    offset += 2;
  }

  return new Blob([buffer], { type: WAV_MIME_TYPE });
};

export async function transformBlobToPlayableFormat({
  blobs,
  sampleRate = DEFAULT_PCM_SAMPLE_RATE,
}: {
  blobs: Blob[];
  sampleRate?: number;
}): Promise<Blob | null> {
  const validBlobs = blobs.filter((blob): blob is Blob => blob instanceof Blob);
  if (!validBlobs.length) return null;

  const allPcm = validBlobs.every(blob => isPcmMimeType(blob.type));

  if (!allPcm) {
    const mimeType = validBlobs[0]?.type || MediaRecorderMimeType.WebmOpus;
    return new Blob(validBlobs, { type: mimeType });
  }

  const pcmArrays = await Promise.all(
    validBlobs.map(async blob => {
      const buffer = await blob.arrayBuffer();
      return new Int16Array(buffer);
    })
  );

  if (!pcmArrays.length) return null;

  const combinedSamples = concatenateInt16Arrays(pcmArrays);
  return encodePcm16ToWav(combinedSamples, sampleRate);
}
